import { Box, Typography } from '@mui/material'
import { usePlanningStore } from '@planning/AppProvider'
import { useTranslate } from '@tolgee/react'
import { ConfirmationDialog, useMinimalsTheme } from '@tom-ui/ui'
import { PATHS, useAsyncFetch, withAuthenticationRequired } from '@tom-ui/utils'
import { observer } from 'mobx-react-lite'
import { FC, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router'
import { ChangesSummary } from '../VesselVisit/Components/atoms/ChangesSummary'
import { RailVisitBulkEditForm } from './Components/Forms/RailBulkEditForm'
import { RailVisitBulkEditHeader } from './Components/Header/RailVisitBulkEditHeader'
import { RailVisitBulkEditFormStore } from './stores/RailVisitBulkEditFormStore'
import { RailVisitBulkEditViewStore } from './stores/RailVisitBulkEditViewStore'

export const RailVisitBulkEditPage: FC = withAuthenticationRequired(
  observer(() => {
    const { t } = useTranslate()
    const navigate = useNavigate()
    const theme = useMinimalsTheme()

    const location = useLocation()
    const { data, category: selectedCategory } = location.state || {}

    const formStore = useMemo(() => new RailVisitBulkEditFormStore(), [])

    const [isCancelDialogOpen, setCancelDialogOpen] = useState(false)
    const [isSaveDialogOpen, setSaveDialogOpen] = useState(false)

    const {
      appViewStore,
      railVisitQueryService,
      railVisitItemStore,
      orderItemStore,
      containerJourneyItemStore,
      containerItemStore,
    } = usePlanningStore()

    const { id, direction: directionFromUrl } = useParams()
    const railVisitId = Number(id)
    const totalSelectedCount = data?.length || 0

    if (!railVisitId || totalSelectedCount === 0) {
      navigate(PATHS.visits.railVisits.url)
    }

    const viewStore = useMemo(() => {
      return new RailVisitBulkEditViewStore(
        containerJourneyItemStore,
        railVisitItemStore,
        railVisitId,
        data,
      )
    }, [containerJourneyItemStore, railVisitId, railVisitItemStore, data])

    useAsyncFetch(async () => {
      await railVisitQueryService.fetchById(railVisitId)
      await orderItemStore.fetchByVisitId(railVisitId)
      await containerItemStore.fetchByVisitId(railVisitId)
    }, [railVisitId, railVisitQueryService, orderItemStore, containerItemStore])

    const onSave = () => {
      const isFormValid = formStore.validate()
      if (isFormValid) {
        setSaveDialogOpen(true)
      }
    }

    const handleConfirmSave = async () => {
      setSaveDialogOpen(false)
      try {
        await formStore.submit(data)

        appViewStore.setShowAlert(
          'success',
          t('bulkUpdateSuccessful', 'Container visits updated successfully.'),
        )
        handleNavigateBack()
      } catch (error) {
        console.error('Failed to bulk update container visits:', error)
        appViewStore.setShowAlert(
          'error',
          t('bulkUpdateFailed', 'Failed to update container visits.'),
        )
      }
    }

    const handleNavigateBack = () => {
      if (location.key !== 'default') {
        navigate(-1)
      } else {
        navigate(PATHS.visits.railVisits.url)
      }
    }

    const onCancel = () => {
      if (formStore.isDirty) {
        setCancelDialogOpen(true)
      } else {
        handleNavigateBack()
      }
    }

    const handleConfirmCancel = () => {
      setCancelDialogOpen(false)
      handleNavigateBack()
    }

    const direction = useMemo(() => {
      if (directionFromUrl === 'load') return 'load'
      return 'discharge'
    }, [directionFromUrl])

    return (
      <Box sx={{ backgroundColor: theme.palette.grey[200] }}>
        {viewStore.railVisitItem && (
          <>
            <RailVisitBulkEditHeader
              category={selectedCategory}
              direction={direction}
              railVisitItem={viewStore.railVisitItem}
              onSave={onSave}
              onCancel={onCancel}
            />

            <RailVisitBulkEditForm formStore={formStore} viewStore={viewStore} />
          </>
        )}

        <ConfirmationDialog
          open={isCancelDialogOpen}
          title={t('cancelBulkEditing', 'Cancel bulk editing?')}
          message={t('yourChangesWillBeLost', 'Your changes will be lost.')}
          primaryActionText={t('yesConfirm', 'Yes, confirm')}
          primaryActionDataCy={'cancel-dialog-bulk-edit-confirm-btn'}
          closeLabel={t('noBackToEditing', 'No, back to editing')}
          onConfirm={handleConfirmCancel}
          onClose={() => setCancelDialogOpen(false)}
        />

        <ConfirmationDialog
          open={isSaveDialogOpen}
          title={t('confirmBulkEditing', 'Confirm bulk editing?')}
          message={
            <Box>
              <Typography gutterBottom>
                {t('youAreAboutToEditVisits', 'You are about to edit {count} container visits.', {
                  count: totalSelectedCount,
                })}
              </Typography>

              <ChangesSummary data={formStore.data} />
            </Box>
          }
          primaryActionText={t('yesConfirm', 'Yes, confirm')}
          primaryActionDisabled={!formStore.hasChanges}
          primaryActionDataCy={'dialog-bulk-edit-confirm-btn'}
          closeLabel={t('noBackToEditing', 'No, back to editing')}
          onConfirm={handleConfirmSave}
          onClose={() => setSaveDialogOpen(false)}
        />
      </Box>
    )
  }),
)
